import { useState, useRef, useEffect, useCallback } from "react";
import { Send, X, Loader2, ExternalLink, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface TemplateOption {
  id: string;
  name: string;
  image_url: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  // Populated when the assistant returns the template-picker response —
  // clicking a thumbnail calls back into the edge function with
  // `action: "finalize_card"` plus the captured card_details.
  templates?: TemplateOption[];
  cardDetails?: Record<string, unknown>;
  // Set once a template has been picked so the thumbnails lock and show
  // the selected state.
  pickedTemplateId?: string;
  // Set on "card_created" messages so the bubble renders a link to the
  // newly created card.
  cardSlug?: string;
}

interface AssistantChatProps {
  onClose: () => void;
}

export function AssistantChat({ onClose }: AssistantChatProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content:
        "Hey there! 👋 I'm Joy, your card creation assistant. Tell me — what are we celebrating, who is it for, and do you know their email? 🎉",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: Message = { role: "user", content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      // Build the conversation payload for the API. Strip chat-only
      // metadata (templates, cardDetails, cardSlug, pickedTemplateId)
      // so the LLM only sees role/content.
      const apiMessages = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const { data, error } = await supabase.functions.invoke("ai-card-assistant", {
        body: {
          messages: apiMessages,
          user_id: user?.id || null,
        },
      });

      if (error) throw error;

      if (data.type === "template_picker") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.content,
            templates: data.templates,
            cardDetails: data.card_details,
          },
        ]);
      } else if (data.type === "card_created") {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.content, cardSlug: data.card_slug },
        ]);
      } else if (data.type === "auth_required") {
        setMessages((prev) => [...prev, { role: "assistant", content: data.content }]);
      } else {
        setMessages((prev) => [...prev, { role: "assistant", content: data.content }]);
      }
    } catch (e) {
      console.error("Assistant error:", e);
      toast({
        variant: "destructive",
        title: "Something went wrong",
        description: "Please try again.",
      });
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, messages, user, toast]);

  // Clicking a template thumbnail finalizes the card with that
  // template_id. We tag the message with `pickedTemplateId` so the
  // gallery stays visible but locks and highlights the chosen design.
  const finalizeWithTemplate = useCallback(
    async (messageIndex: number, template: TemplateOption) => {
      if (finalizing) return;
      const msg = messages[messageIndex];
      if (!msg?.cardDetails) return;

      setFinalizing(true);
      setMessages((prev) =>
        prev.map((m, i) => (i === messageIndex ? { ...m, pickedTemplateId: template.id } : m)),
      );

      try {
        const { data, error } = await supabase.functions.invoke("ai-card-assistant", {
          body: {
            action: "finalize_card",
            card_details: msg.cardDetails,
            template_id: template.id,
            user_id: user?.id || null,
          },
        });
        if (error) throw error;

        if (data.type === "card_created") {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: data.content, cardSlug: data.card_slug },
          ]);
        } else if (data.type === "auth_required") {
          setMessages((prev) => [...prev, { role: "assistant", content: data.content }]);
        } else if (data.content) {
          setMessages((prev) => [...prev, { role: "assistant", content: data.content }]);
        }
      } catch (e) {
        console.error("Finalize error:", e);
        // Roll back the picked state so the user can try again.
        setMessages((prev) =>
          prev.map((m, i) => (i === messageIndex ? { ...m, pickedTemplateId: undefined } : m)),
        );
        toast({
          variant: "destructive",
          title: "Couldn't create the card",
          description: "Please try picking a template again.",
        });
      } finally {
        setFinalizing(false);
      }
    },
    [finalizing, messages, user, toast],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col h-full bg-background rounded-2xl shadow-2xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold">
            J
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Joy</p>
            <p className="text-[11px] text-muted-foreground">Card creation assistant</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted transition-colors">
          <X size={18} className="text-muted-foreground" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground rounded-br-md"
                  : "bg-muted text-foreground rounded-bl-md"
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.content}</p>

              {/* In-chat template picker gallery. Rendered when the
                  assistant returned a template_picker response. Clicking
                  a thumbnail calls finalize_card. */}
              {msg.templates && msg.templates.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {msg.templates.map((t) => {
                    const isPicked = msg.pickedTemplateId === t.id;
                    const isLocked = !!msg.pickedTemplateId && !isPicked;
                    return (
                      <button
                        key={t.id}
                        onClick={() => finalizeWithTemplate(i, t)}
                        disabled={!!msg.pickedTemplateId || finalizing}
                        className={`group relative aspect-[3/4] overflow-hidden rounded-xl border-2 transition-all ${
                          isPicked
                            ? "border-primary ring-2 ring-primary/30"
                            : isLocked
                              ? "border-transparent opacity-40"
                              : "border-transparent hover:border-primary/60"
                        }`}
                      >
                        <img
                          src={t.image_url}
                          alt={t.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                        {isPicked && (
                          <div className="absolute inset-0 flex items-center justify-center bg-primary/30">
                            {finalizing ? (
                              <Loader2 size={20} className="animate-spin text-white" />
                            ) : (
                              <Check size={20} className="text-white" />
                            )}
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
                          <p className="text-[10px] font-medium text-white truncate text-left">
                            {t.name}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {msg.cardSlug && (
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/card/${msg.cardSlug}/manage`);
                  }}
                  className="mt-2 flex items-center gap-1.5 text-xs font-medium underline underline-offset-2 opacity-90 hover:opacity-100"
                >
                  <ExternalLink size={12} />
                  Open your card
                </button>
              )}
            </div>
          </div>
        ))}
        {(loading || finalizing) && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 size={16} className="animate-spin text-muted-foreground" />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-3 border-t border-border">
        <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-1.5">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            disabled={loading || finalizing}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none py-1.5"
          />
          <button
            onClick={send}
            disabled={loading || finalizing || !input.trim()}
            className="p-1.5 rounded-full bg-primary text-primary-foreground disabled:opacity-40 transition-opacity"
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
